import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileText, Download, FileSpreadsheet, Loader2, BarChart3, ChevronDown, ChevronUp, ArrowLeft, Eye, FileBarChart } from "lucide-react";
import { generateReportPDF, generateAnalyticsPDF, COMPANY } from "@/lib/pdfGenerator";
import ShiftSummaryReport from "@/components/shared/ShiftSummaryReport";
import logger from "@/lib/logger";

export default function Reports() {
  const [tab, setTab] = useState("registers");
  const [registers, setRegisters] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedReport, setSelectedReport] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const { location: defaultLocation, user } = useAuth();
  const isManagerOrAdmin = user?.role === "admin" || user?.role === "manager";
  const [reportLocation, setReportLocation] = useState("");
  const [filterParty, setFilterParty] = useState("");
  const [filterProcess, setFilterProcess] = useState("");
  const [filterGasType, setFilterGasType] = useState("");
  const [filterDestination, setFilterDestination] = useState("");
  const [filterVehicle, setFilterVehicle] = useState("");
  const [filterOptions, setFilterOptions] = useState({ parties: [], processes: [], gas_types: [], destinations: [], vehicles: [] });

  const isJobworkReport = selectedReport === "fgat_08" || selectedReport === "fgat_12";
  const isCylinderReport = selectedReport === "fgat_03";
  const isEmployeeReport = selectedReport === "fgat_20";
  const isFuelReport = selectedReport === "fgat_21";
  const isVehicleTcoReport = selectedReport === "fgat_26";
  const hasExtraFilters = isJobworkReport || isCylinderReport || isEmployeeReport || isFuelReport || isVehicleTcoReport;

  const location = reportLocation && reportLocation !== "all" ? reportLocation : "";

  useEffect(() => {
    api.get("/reports/registers").then(({ data }) => setRegisters(data.registers || [])).catch((err) => { logger.error("Failed to load registers:", err); });
  }, []);

  // Load filter options for jobwork/cylinder registers
  useEffect(() => {
    if (!hasExtraFilters) { setFilterOptions({ parties: [], processes: [], gas_types: [], destinations: [], vehicles: [] }); return; }
    api.get(`/reports/filter-options/${selectedReport}`, { params: { location } })
      .then(({ data }) => setFilterOptions(data))
      .catch(() => setFilterOptions({ parties: [], processes: [], gas_types: [], destinations: [], vehicles: [] }));
  }, [selectedReport, location, hasExtraFilters]);

  const handleTabChange = useCallback((newTab) => {
    setTab(newTab);
    if (newTab === "registers") { setReportData(null); setSelectedReport(""); }
  }, []);

  const handleBackToList = () => { setReportData(null); setSelectedReport(""); };

  const handleGenerate = async () => {
    if (!selectedReport) { toast.error("Select a register"); return; }
    setLoading(true);
    try {
      const params = { location };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (filterParty && filterParty !== "all") params.party_name = filterParty;
      if (filterProcess && filterProcess !== "all") params.process_type = filterProcess;
      if (filterGasType && filterGasType !== "all") params.gas_type = filterGasType;
      if (filterDestination && filterDestination !== "all") params.destination = filterDestination;
      if (filterVehicle && filterVehicle !== "all") params.vehicle_no = filterVehicle;
      const { data } = await api.get(`/reports/data/${selectedReport}`, { params });
      setReportData(data);
    } catch { toast.error("Failed to generate report"); }
    setLoading(false);
  };

  const handleExportCSV = async () => {
    if (!selectedReport) return;
    try {
      const params = { location };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (filterParty && filterParty !== "all") params.party_name = filterParty;
      if (filterProcess && filterProcess !== "all") params.process_type = filterProcess;
      if (filterGasType && filterGasType !== "all") params.gas_type = filterGasType;
      if (filterDestination && filterDestination !== "all") params.destination = filterDestination;
      if (filterVehicle && filterVehicle !== "all") params.vehicle_no = filterVehicle;
      const response = await api.get(`/reports/export/${selectedReport}`, { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      const reg = registers.find(r => r.key === selectedReport);
      a.download = `${reg?.fgat_no || selectedReport}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
  };

  const handleExportPDF = () => {
    if (!reportData) return;
    const reg = registers.find(r => r.key === selectedReport);
    generateReportPDF({
      fgatNo: reportData.fgat_no,
      title: reportData.title,
      columns: reportData.columns || [],
      entries: reportData.entries || [],
      location,
      dateFrom,
      dateTo,
      filename: `${reg?.fgat_no || selectedReport}_${new Date().toISOString().slice(0, 10)}`,
    });
  };

  const handleLoadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const params = { location };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/reports/analytics/visitors", { params });
      setAnalytics(data);
    } catch { toast.error("Failed to load analytics"); }
    setAnalyticsLoading(false);
  };

  const handleExportAnalyticsPDF = () => {
    generateAnalyticsPDF({
      analytics: analytics?.analytics || [],
      location,
      dateFrom,
      dateTo,
    });
  };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Chivo" }}>Reports</h1>
        <p className="text-xs sm:text-sm text-gray-500">F-GAT Register Reports &middot; {COMPANY.name}</p>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList className="h-10 sm:h-11 bg-[#1B3C87]/5">
          <TabsTrigger value="registers" className="h-8 sm:h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-[#1B3C87] data-[state=active]:text-white" data-testid="tab-registers">
            <FileText size={14} className="mr-1" /> F-GAT Registers
          </TabsTrigger>
          <TabsTrigger value="analytics" className="h-8 sm:h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-[#1B3C87] data-[state=active]:text-white" data-testid="tab-analytics">
            <BarChart3 size={14} className="mr-1" /> Visitor Analytics
          </TabsTrigger>
          {isManagerOrAdmin && (
            <TabsTrigger value="shiftsummary" className="h-8 sm:h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-[#1B3C87] data-[state=active]:text-white" data-testid="tab-shiftsummary">
              <FileBarChart size={14} className="mr-1" /> Shift Summary
            </TabsTrigger>
          )}
        </TabsList>

        {tab !== "shiftsummary" && (
        <Card className="p-4 mt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
            {tab === "registers" && (
              <div className="lg:col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Register</Label>
                <Select value={selectedReport} onValueChange={v => { setSelectedReport(v); setReportData(null); setFilterParty(""); setFilterProcess(""); setFilterGasType(""); setFilterDestination(""); setFilterVehicle(""); }}>
                  <SelectTrigger className="h-11" data-testid="report-type-select"><SelectValue placeholder="Select F-GAT register" /></SelectTrigger>
                  <SelectContent>
                    {registers.map(r => (
                      <SelectItem key={r.key} value={r.key}>
                        <span className="flex items-center gap-2">
                          <span className="text-xs text-[#E31E24] font-mono font-bold">{r.fgat_no}</span>
                          <span className="truncate">{r.title}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {tab === "analytics" && <div className="lg:col-span-2" />}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Location</Label>
              <Select value={reportLocation} onValueChange={v => { setReportLocation(v); setReportData(null); }}>
                <SelectTrigger className="h-11" data-testid="report-location-select"><SelectValue placeholder="All Locations" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  <SelectItem value="Focal Point">Focal Point</SelectItem>
                  <SelectItem value="Sahnewal">Sahnewal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">From Date</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-11" data-testid="report-date-from" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">To Date</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-11" data-testid="report-date-to" />
            </div>
            <Button onClick={tab === "registers" ? handleGenerate : handleLoadAnalytics} disabled={loading || analyticsLoading} className="bg-[#1B3C87] hover:bg-[#2650A8] h-11" data-testid="generate-report-btn">
              {(loading || analyticsLoading) ? <Loader2 size={16} className="animate-spin mr-2" /> : <FileText size={16} className="mr-2" />}
              Generate
            </Button>
          </div>

          {/* Job Work / Cylinder specific filters */}
          {tab === "registers" && hasExtraFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Party Name</Label>
                <Select value={filterParty} onValueChange={v => { setFilterParty(v); setReportData(null); }}>
                  <SelectTrigger className="h-11" data-testid="filter-party-select"><SelectValue placeholder="All Parties" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Parties</SelectItem>
                    {filterOptions.parties.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isJobworkReport && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Process</Label>
                  <Select value={filterProcess} onValueChange={v => { setFilterProcess(v); setReportData(null); }}>
                    <SelectTrigger className="h-11" data-testid="filter-process-select"><SelectValue placeholder="All Processes" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Processes</SelectItem>
                      {filterOptions.processes.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isCylinderReport && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Gas Type</Label>
                  <Select value={filterGasType} onValueChange={v => { setFilterGasType(v); setReportData(null); }}>
                    <SelectTrigger className="h-11" data-testid="filter-gas-type-select"><SelectValue placeholder="All Gas Types" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Gas Types</SelectItem>
                      {filterOptions.gas_types.map(g => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isEmployeeReport && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Destination</Label>
                  <Select value={filterDestination} onValueChange={v => { setFilterDestination(v); setReportData(null); }}>
                    <SelectTrigger className="h-11" data-testid="filter-destination-select"><SelectValue placeholder="All Destinations" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Destinations</SelectItem>
                      {filterOptions.destinations.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {(isFuelReport || isVehicleTcoReport) && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle</Label>
                  <Select value={filterVehicle} onValueChange={v => { setFilterVehicle(v); setReportData(null); }}>
                    <SelectTrigger className="h-11" data-testid="filter-vehicle-select"><SelectValue placeholder="All Vehicles" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Vehicles</SelectItem>
                      {filterOptions.vehicles.map(v => (
                        <SelectItem key={v} value={v}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
        </Card>
        )}

        {isManagerOrAdmin && (
          <TabsContent value="shiftsummary">
            <div className="mt-3">
              <ShiftSummaryReport />
            </div>
          </TabsContent>
        )}

        <TabsContent value="registers">
          {reportData && (
            <Card className="p-0 mt-3 overflow-hidden" data-testid="report-results">
              {/* Toolbar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 pb-3 gap-2 border-b">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="sm" onClick={handleBackToList} className="h-8 px-2" data-testid="back-to-list-btn"><ArrowLeft size={16} /></Button>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-bold text-[#E31E24] bg-red-50 px-2 py-0.5 rounded">{reportData.fgat_no}</span>
                      <h3 className="font-bold text-sm sm:text-base" style={{ fontFamily: "Chivo" }}>{reportData.title}</h3>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{reportData.total} records &middot; {location}</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" onClick={handleExportCSV} className="h-9 text-xs" data-testid="export-csv-btn"><FileSpreadsheet size={14} className="mr-1" /> CSV</Button>
                  <Button onClick={handleExportPDF} className="bg-[#E31E24] hover:bg-[#C51A1F] h-9 text-xs" data-testid="export-pdf-btn"><Download size={14} className="mr-1" /> Download PDF</Button>
                </div>
              </div>

              {/* In-Browser Preview — mirrors PDF layout */}
              <div className="p-4 sm:p-6 overflow-x-auto" data-testid="report-preview">
                <div className="min-w-[900px] border border-gray-200 rounded-lg shadow-sm bg-white">
                  {/* Company Header (mirrors PDF) */}
                  <div className="text-center pt-5 pb-3 px-4">
                    <h2 className="text-lg font-bold" style={{ color: "#003399", fontFamily: "Chivo" }}>{COMPANY.name}</h2>
                    <p className="text-[10px] text-gray-500 mt-0.5">{COMPANY.addr}</p>
                    <p className="text-[9px] text-gray-400">{COMPANY.email} &nbsp; {COMPANY.phone}</p>
                    <p className="text-[9px] text-gray-400">{COMPANY.web}</p>
                    <div className="h-[2px] bg-[#003399] my-2 mx-auto" style={{ maxWidth: "95%" }} />
                    <p className="text-[9px] text-emerald-600 font-medium">{COMPANY.cert}</p>
                  </div>

                  {/* Report Title & Filter */}
                  <div className="flex items-center justify-between px-5 pb-2">
                    <span className="text-xs font-mono font-bold text-[#E31E24]">{reportData.fgat_no}</span>
                    <h3 className="text-sm font-bold" style={{ fontFamily: "Chivo" }}>{reportData.title}</h3>
                    <span className="text-[10px] text-gray-400">
                      {location || "All"}{dateFrom ? ` | From: ${dateFrom}` : ""}{dateTo ? ` | To: ${dateTo}` : ""}
                    </span>
                  </div>

                  {/* Data Table */}
                  <div className="px-3 pb-4">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
                          {(reportData.columns || []).map(col => (
                            <TableHead key={col} className="text-white text-[10px] sm:text-xs uppercase tracking-wider font-bold px-2 sm:px-3 whitespace-normal">{col}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(reportData.entries || []).length === 0 && (
                          <TableRow><TableCell colSpan={(reportData.columns || []).length} className="text-center py-8 text-gray-400">No data for selected period</TableCell></TableRow>
                        )}
                        {(reportData.entries || []).map((entry, i) => (
                          <TableRow key={entry.ref_no || entry["S.No"] || `row-${i}`} className={i % 2 === 0 ? "bg-slate-50/60 hover:bg-blue-50/50" : "hover:bg-blue-50/50"}>
                            {(reportData.columns || []).map(col => (
                              <TableCell key={col} className="text-[11px] sm:text-xs px-2 sm:px-3 whitespace-normal break-words max-w-[220px]">{entry[col] || "\u2014"}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {!reportData && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
              {registers.map(r => (
                <Card key={r.key} className={`p-3 sm:p-4 hover:shadow-md transition cursor-pointer border-l-4 ${selectedReport === r.key ? "border-[#E31E24] bg-red-50/30" : "border-[#1B3C87]"}`} onClick={() => setSelectedReport(r.key)} data-testid={`report-card-${r.key}`}>
                  <div className="flex items-start gap-3">
                    <span className="text-[10px] font-mono font-bold text-[#E31E24] bg-red-50 px-1.5 py-0.5 rounded shrink-0">{r.fgat_no}</span>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-xs sm:text-sm leading-tight">{r.title}</h4>
                      <p className="text-[10px] text-gray-400 mt-0.5">{r.column_count} columns</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="analytics">
          {analytics && (
            <Card className="p-4 sm:p-5 mt-3" data-testid="analytics-results">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-2">
                <div>
                  <h3 className="font-bold" style={{ fontFamily: "Chivo" }}>Company-wise Visitor Analysis</h3>
                  <p className="text-xs text-gray-500">{analytics.total_companies} companies &middot; {location}</p>
                </div>
                <Button onClick={handleExportAnalyticsPDF} className="bg-[#E31E24] hover:bg-[#C51A1F] h-9 text-xs" data-testid="export-analytics-pdf"><Download size={14} className="mr-1" /> Download PDF</Button>
              </div>
              <div className="space-y-2">
                {(analytics.analytics || []).map((comp) => (
                  <div key={comp.company} className="border rounded overflow-hidden">
                    <div className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => setExpandedCompany(expandedCompany === comp.company ? null : comp.company)} data-testid={`analytics-company-${comp.company}`}>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-semibold text-sm">{comp.company}</span>
                        <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded border">{comp.total_visits} visits</span>
                        <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded border">{comp.total_hours} hrs</span>
                      </div>
                      {expandedCompany === comp.company ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                    {expandedCompany === comp.company && (
                      <div className="p-3 border-t">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-[#1B3C87]/10">
                              <TableHead className="text-xs font-bold">Visitor Name</TableHead>
                              <TableHead className="text-xs font-bold">Mobile</TableHead>
                              <TableHead className="text-xs font-bold">Visits</TableHead>
                              <TableHead className="text-xs font-bold">Total Hours</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {comp.visitors.map((v) => (
                              <TableRow key={`${v.name}-${v.mobile}`} className="hover:bg-blue-50/50">
                                <TableCell className="text-xs font-medium">{v.name}</TableCell>
                                <TableCell className="text-xs">{v.mobile || "\u2014"}</TableCell>
                                <TableCell className="text-xs">{v.visits}</TableCell>
                                <TableCell className="text-xs">{v.hours} hrs</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
          {!analytics && (
            <div className="text-center py-12 text-gray-400 mt-3">
              <BarChart3 size={48} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Select a date range and click Generate to view visitor analytics</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
